// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import * as React from 'react';
import { Track } from 'livekit-client';
import { MediaDeviceMenu } from './media-device-menu';
import {
  ChatIcon,
  ChatToggle,
  StartMediaButton,
  TrackToggle,
  useLocalParticipantPermissions,
  useMaybeLayoutContext,
  useMaybeRoomContext,
  usePersistentUserChoices,
} from '@livekit/components-react';
import { TrackToggleMic } from './track-toggle-mic.tsx';
import { supportsScreenSharing } from '@livekit/components-core';
import { cn } from '@/lib/utils';
import ScreenRecorder from './screen-recorder';
import { User, UserPlan } from '@/models/user';
import { RaiseHandButton } from '@/components/raise-hand-button';
import { useCall } from '@/context/call-context/use-call.ts';
import { PhoneOffIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '@/config';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { useSettings } from '@/context/settings-context/settings-context';
import { isPardusBrowser } from '@/components/check-user-agent.tsx';
import { useGlobalAudio } from '@/context/global-audio-context/use-global-audio.ts';
import { useGlobalSFX } from '@/context/sfx-context/use-sfx.ts';
import { ScreenShareToggle } from '@/components/call/screen-share-toggle.tsx';
import AudioOutputSelector from '@/components/call/audio-output-selector.tsx';
import { useBreakpoint } from '@/hooks/use-breakpoint.ts';
import { GearIcon } from '@radix-ui/react-icons';
import { SettingsMenuToggle } from '@/components/call/controls/settings-menu-toggle.tsx';
import { CaptionToggleButton } from '@/context/caption-context/caption-toggle-button.tsx';
import { ChannelType } from "@/models";

/** @public */
export type ControlBarControls = {
  microphone?: boolean;
  camera?: boolean;
  chat?: boolean;
  screenShare?: boolean;
  leave?: boolean;
  settings?: boolean;
  record?: boolean;
  captions?: boolean;
};

/** @public */
export interface ControlBarProps extends React.HTMLAttributes<HTMLDivElement> {
  onDeviceError?: (error: { source: Track.Source; error: Error }) => void;
  variation?: 'minimal' | 'verbose' | 'textOnly';
  controls?: ControlBarControls;
  /**
   * If `true`, the user's device choices will be persisted.
   * This will enables the user to have the same device choices when they rejoin the room.
   * @defaultValue true
   * @alpha
   */
  saveUserChoices?: boolean;
  serverId?: string;
  chatId?: string;
  onEndCall?: () => void;
  plan?: UserPlan | null;
  channelType?: ChannelType | null;
}

/**
 * The `ControlBar` prefab gives the user the basic user interface to control their
 * media devices (camera, microphone and screen share), open the `Chat` and leave the room.
 *
 * @remarks
 * This component is build with other LiveKit components like `TrackToggle`,
 * `DeviceSelectorButton`, `DisconnectButton` and `StartAudio`.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <ControlBar />
 * </LiveKitRoom>
 * ```
 * @public
 *
 * TODO: add screen type selector from browser when in electron. -> publish
 */
export function ControlBar({
  variation,
  controls,
  serverId,
  chatId,
  saveUserChoices = true,
  onDeviceError,
  onEndCall,
  plan,
  channelType
}: ControlBarProps) {
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const layoutContext = useMaybeLayoutContext();
  React.useEffect(() => {
    if (layoutContext?.widget.state?.showChat !== undefined) {
      setIsChatOpen(layoutContext?.widget.state?.showChat);
    }
  }, [layoutContext?.widget.state?.showChat]);
  const { deviceId, setDeviceId } = useGlobalAudio();
  const { selectedAudioInputDeviceId, setSelectedAudioInputDeviceId } =
    useSettings();
  const room = useMaybeRoomContext();
  const [isScreenSharing, setIsScreenSharing] = React.useState(false);
  const { isMd: isDesktop } = useBreakpoint('md');
  const isTooLittleSpace = window.matchMedia(`(max-width: ${isChatOpen ? 1000 : 760}px)`);
  const { screenShare } = useCall();
  const isSharing = screenShare.isSharing;

  const defaultVariation = isTooLittleSpace ? 'minimal' : 'verbose';
  variation ??= defaultVariation;

  const visibleControls = { leave: true, ...controls };

  const localPermissions = useLocalParticipantPermissions();

  const { captureSystemAudio } = useSettings();

  const { activeCall, docked, setMicrophoneActive, setEndCallClicked, endCall } = useCall();
  const sfx = useGlobalSFX();
  const isAudioChannel = ChannelType.AUDIO === channelType;
  const isVideoChannel = ChannelType.VIDEO === channelType;
  const isTribuneChannel = ChannelType.TRIBUNE === channelType;

  const FPS_OPTIONS = [3, 5, 15, 30] as const;
  const [fpsOpen, setFpsOpen] = React.useState(false);
  const [fps, setFps] = React.useState<3 | 5 | 15 | 30 | 60>(30);

  const FPS_TO_PRESET = {
    3: 'slideshow',
    5: 'ultralow',
    15: 'low',
    30: 'medium',
    60: 'high',
  } as const;

  const onFpsChange = async (value: 3 | 5 | 15 | 30 | 60) => {
    setFps(value);
    const preset = FPS_TO_PRESET[value];
    if (!preset) return;

    try {
      await updateScreenShareNetworkQuality(preset);
    } catch (e) {
      console.error('Failed to update FPS preset:', e);
    }
  };

  function applyFramerateToAllEncodings(parameters: RTCRtpSendParameters, fps: number) {
    if (!parameters.encodings?.length) return parameters;

    parameters.encodings.forEach((enc) => {
      enc.maxFramerate = fps;
    });

    return parameters;
  }
  /**
   * Dynamic switch feature
   * @param preset
   */
  async function updateScreenShareNetworkQuality(
    preset: 'slideshow' | 'ultralow' | 'low' | 'medium' | 'high'
  ) {
    const publication = room?.localParticipant.getTrackPublication(Track.Source.ScreenShare);
    if (!publication) return;

    const sender = publication.track?.sender;
    if (!sender) return;

    const parameters = sender.getParameters();
    if (!parameters.encodings?.length) return;

    let maxFps = 30;

    switch (preset) {
      case 'slideshow':
        parameters.encodings.forEach((enc) => {
          enc.maxBitrate = 200_000;
        });
        maxFps = 3;
        break;
      case 'ultralow':
        parameters.encodings.forEach((enc) => {
          enc.maxBitrate = 800_000;
        });
        maxFps = 5;
        break;
      case 'low':
        parameters.encodings.forEach((enc) => {
          enc.maxBitrate = 1_500_000;
        });
        maxFps = 15;
        break;
      case 'medium':
        parameters.encodings.forEach((enc) => {
          enc.maxBitrate = 4_000_000;
        });
        maxFps = 30;
        // parameters.encodings[0].priority = 'medium';
        break;
      case 'high':
        parameters.encodings.forEach((enc) => {
          enc.maxBitrate = 8_000_000;
        });
        maxFps = 60;
        // parameters.encodings[0].maxFramerate = 60;
        parameters.encodings[0].priority = 'high';
        break;
    }
    applyFramerateToAllEncodings(parameters, maxFps);

    await sender.setParameters(parameters);
  }

  React.useEffect(() => {
    const publication = room?.localParticipant.getTrackPublication(Track.Source.ScreenShare);
    setIsScreenSharing(!!publication);

    const handleTrackChange = () => {
      const pub = room?.localParticipant.getTrackPublication(Track.Source.ScreenShare);
      setIsScreenSharing(!!pub);
    };

    room?.localParticipant.on('localTrackPublished', handleTrackChange);
    room?.localParticipant.on('localTrackUnpublished', handleTrackChange);

    return () => {
      room?.localParticipant.off('localTrackPublished', handleTrackChange);
      room?.localParticipant.off('localTrackUnpublished', handleTrackChange);
    };
  }, [room]);

  if (!localPermissions) {
    visibleControls.camera = false;
    visibleControls.chat = false;
    visibleControls.microphone = false;
    visibleControls.screenShare = false;
    visibleControls.record = false;
    visibleControls.captions = false;
  } else {
    visibleControls.camera ??= localPermissions.canPublish;
    visibleControls.microphone ??= localPermissions.canPublish;
    visibleControls.screenShare ??= localPermissions.canPublish;
    visibleControls.chat ??=
      localPermissions.canPublishData && controls?.chat && plan !== UserPlan.FREE;
    visibleControls.record ??= localPermissions.canPublish && plan !== UserPlan.FREE;
    visibleControls.captions ??= plan !== UserPlan.FREE;
  }

  React.useEffect(() => {
    if (!room || !selectedAudioInputDeviceId) return;

    room.switchActiveDevice('audioinput', selectedAudioInputDeviceId).catch(console.error);
  }, [selectedAudioInputDeviceId, room]);

  const showIcon = React.useMemo(
    () => variation === 'minimal' || variation === 'verbose',
    [variation]
  );
  const showText = React.useMemo(
    () => variation === 'textOnly' || variation === 'verbose',
    [variation]
  );

  const browserSupportsScreenSharing = supportsScreenSharing();

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: !saveUserChoices });

  const microphoneOnChange = React.useCallback(
    (enabled: boolean, isUserInitiated: boolean) => {
      isUserInitiated && !enabled && sfx.microphoneMute.play();
      isUserInitiated && enabled && sfx.microphoneUnmute.play();

      isUserInitiated && setMicrophoneActive(enabled);
      isUserInitiated ? saveAudioInputEnabled(enabled) : null;
    },
    [saveAudioInputEnabled, sfx]
  );

  const cameraOnChange = React.useCallback(
    (enabled: boolean, isUserInitiated: boolean) =>
      isUserInitiated ? saveVideoInputEnabled(enabled) : null,
    [saveVideoInputEnabled]
  );

  const endCallClicked = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setEndCallClicked(true);

    // endCall();
    if (onEndCall) onEndCall();
    navigate(`/servers/${activeCall?.serverId}`);
    sfx.disappear.play();
  };

  const baseButtonGroupStyles =
    'z-50 rounded-sm bg-foreground-100 hover:bg-foreground-400 duration-200 items-center justify-center flex';

  return (
    <div
      className={cn(
        'lk-control-bar w-full md:w-auto',
        docked ? 'grid grid-cols-4' : 'grid grid-cols-4 relative',
        'lg:grid-rows-1 mx-auto items-center justify-center',
        docked ? 'gap-1' : 'gap-0'
      )}
    >
      {visibleControls.microphone && (
        <div className={cn(baseButtonGroupStyles, 'lk-button-group')}>
          {/*TODO: use for web too*/}
          {isPardusBrowser() ? (
            <TrackToggleMic
              source={Track.Source.Microphone}
              showIcon={showIcon}
              onChange={microphoneOnChange}
              onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Microphone, error })}
              className={cn(!docked && 'w-4 h-6', 'relative group')}
            >
              {showText && 'Microphone'}
              <span
                className="z-50 absolute -top-8 left-1/2 -translate-x-1/2
                   opacity-0 group-hover:opacity-100
                   text-[10px] font-mono
                   rounded whitespace-nowrap
                   transition-opacity duration-150 pointer-events-none shadow"
              >
                <kbd className="ml-1 px-1 py-0.5 text-[10px] font-mono rounded bg-foreground-200 text-foreground-700">
                  Ctrl+Shift+M
                </kbd>
              </span>
            </TrackToggleMic>
          ) : (
            <TrackToggle
              source={Track.Source.Microphone}
              showIcon={showIcon}
              onChange={microphoneOnChange}
              onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Microphone, error })}
              className={cn(!docked && 'w-4 h-6', 'relative group')}
            >
              {showText && 'Microphone'}
              <span
                className="z-50 absolute -top-8 left-1/2 -translate-x-1/2
                   opacity-0 group-hover:opacity-100
                   text-[10px] font-mono
                   rounded whitespace-nowrap
                   transition-opacity duration-150 pointer-events-none shadow"
              >
                {isPardusBrowser() ? (
                  <kbd className="ml-1 rounded bg-foreground-200 px-1 py-0.5 font-mono text-[10px] text-foreground-700">
                    Shift+~/Shift+ё
                  </kbd>
                ) : (
                  <kbd className="ml-1 px-1 py-0.5 text-[10px] font-mono rounded bg-foreground-200 text-foreground-700">
                    Ctrl+Shift+M
                  </kbd>
                )}
              </span>
            </TrackToggle>
          )}
          {docked && (
            <div className={baseButtonGroupStyles}>
              <MediaDeviceMenu
                kind="audioinput"
                initialSelection={selectedAudioInputDeviceId}
                onActiveDeviceChange={(_kind, deviceId) => {
                  const selectedId = deviceId ?? undefined;
                  saveAudioInputDeviceId(selectedId);
                  setDeviceId(selectedId); // only for live mic visual state in profile

                  setSelectedAudioInputDeviceId(selectedId ?? 'default'); // actually saving in localStorage
                }}
              />
            </div>
          )}
        </div>
      )}
      {visibleControls.camera && !isAudioChannel && (
        <div className={cn(baseButtonGroupStyles, 'lk-button-group')}>
          <TrackToggle
            source={Track.Source.Camera}
            showIcon={showIcon}
            onChange={cameraOnChange}
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Camera, error })}
            className={cn(!docked && 'w-2 h-6', 'relative group')}
          >
            {showText && 'Camera'}
            <span
              className="z-50 absolute -top-8 left-1/2 -translate-x-1/2
                   opacity-0 group-hover:opacity-100
                   text-[10px] font-mono
                   rounded whitespace-nowrap
                   transition-opacity duration-150 pointer-events-none shadow"
            >
              <kbd className="ml-1 rounded bg-foreground-200 px-1 py-0.5 font-mono text-[10px] text-foreground-700">
                Ctrl+Shift+C
              </kbd>
            </span>
          </TrackToggle>
          {docked && (
            <MediaDeviceMenu
              kind="videoinput"
              onActiveDeviceChange={(_kind, deviceId) => saveVideoInputDeviceId(deviceId ?? '')}
            />
          )}
        </div>
      )}
      {docked && isDesktop && (
        <div className={baseButtonGroupStyles}>
          <AudioOutputSelector />
        </div>
      )}
      {visibleControls.screenShare &&
        !isAudioChannel &&
        browserSupportsScreenSharing &&
        room && (
        <div className={baseButtonGroupStyles}>
          <ScreenShareToggle captureSystemAudio={captureSystemAudio} docked={docked} />
        </div>
      )}
      {/*{docked && isDesktop && isSharing && (*/}
      {/*  <div className={baseButtonGroupStyles}>*/}
      {/*    <select*/}
      {/*      value={fps}*/}
      {/*      onChange={(e) => onFpsChange(Number(e.target.value) as any)}*/}
      {/*      // TODO: fix styles on dark mode*/}
      {/*      className="text-xs outline-none"*/}
      {/*    >*/}
      {/*      <option value={3}>3 FPS</option>*/}
      {/*      <option value={5}>5 FPS</option>*/}
      {/*      <option value={15}>15 FPS</option>*/}
      {/*      <option value={30}>30 FPS</option>*/}
      {/*      /!*<option value={60}>60 FPS</option>*!/*/}
      {/*    </select>*/}
      {/*  </div>*/}
      {/*)}*/}
      {visibleControls.captions && docked && isDesktop && (
        <div className="hidden md:block">
          <CaptionToggleButton />
        </div>
      )}
      {visibleControls.record && docked && (
        <div className="hidden md:block">
          <ScreenRecorder />
        </div>
      )}
      {serverId && chatId && docked && (
        <div className="hidden md:block">
          <RaiseHandButton channelId={chatId} className={cn(!docked ? 'w-4 h-4' : 'h-6 w-6')} />
        </div>
      )}
      {/*WARN: control bar should be in LayoutContext to use it, now SettingsMenu CANNOT BE USED due
      to ControlBar location is out of VideoConference*/}
      {visibleControls.settings && (
        <SettingsMenuToggle>
          {showIcon && <GearIcon />}
          {showText && 'Settings'}
        </SettingsMenuToggle>
      )}
      {visibleControls.chat && (
        <ChatToggle>
          {showIcon && <ChatIcon />}
          {showText && 'Chat'}
        </ChatToggle>
      )}
      {!docked && (
        <button
          onClick={endCallClicked}
          onTouchEnd={endCallClicked}
          className="items-center rounded-sm bg-foreground-100 px-2 py-1 font-bold text-red-500 transition-colors duration-200 hover:bg-foreground-300"
        >
          <PhoneOffIcon className="size-4" />
        </button>
      )}
      {visibleControls.leave && docked && (
        <button
          onClick={endCallClicked}
          onTouchEnd={endCallClicked}
          className="text-red-500 hover:bg-foreground-300 bg-foreground-100 font-bold py-1 px-2 rounded-sm items-center transition-colors duration-200"
        >
          {showIcon && <PhoneOffIcon />}
          {showText && 'Leave'}
        </button>
      )}
      <StartMediaButton />
    </div>
  );
}
